import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  FileText, 
  MessageSquare, 
  ShoppingBag,
  Settings,
  Activity,
  TrendingUp,
  DollarSign,
  Eye,
  Plus,
  Search,
  Filter,
  Download,
  RefreshCw,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Sun,
  Moon,
  Bell,
  User,
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Globe,
  Home,
  Star,
  Tag,
  CreditCard,
  Package,
  Calculator,
  AlertTriangle,
  FormInput,
  Zap,
  Layout,
  Palette,
  Database,
  Shield,
  BarChart,
  Mail,
  Send,
  Bot,
  ShoppingCart
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useApiCall } from '../../hooks/useApi';
import { useOrderNotifications } from '../../hooks/useOrderNotifications';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import { adminAPI, userAPI, projectAPI, contactAPI, blogAPI, productAPI } from '../../services/api';
import UsersManagement from '../Admin/UsersManagement';
import ProjectsManagement from '../Admin/ProjectsManagement';
import ProjectTypesManagement from '../Admin/ProjectTypesManagement';
import ContactsManagement from '../Admin/ContactsManagement';
import BlogManagement from '../Admin/BlogManagement';
import ProductsManagement from '../Admin/ProductsManagement';
import ProductCategoriesManagement from '../Admin/ProductCategoriesManagement';
import AnalyticsDashboard from '../Admin/AnalyticsDashboard';
import SettingsManagement from '../Admin/SettingsManagement';
import ServicesManagement from '../Admin/ServicesManagement';
import PortfolioManagement from '../Admin/PortfolioManagement';
import TestimonialsManagement from '../Admin/TestimonialsManagement';
import HomePageManagement from '../Admin/HomePageManagement';
import AIManagement from '../Admin/AIManagement';
import AboutUsManagement from '../Admin/AboutUsManagement';
import ServicesPageManagement from '../Admin/ServicesPageManagement';
import ProductsPageManagement from '../Admin/ProductsPageManagement';
import PortfolioPageManagement from '../Admin/PortfolioPageManagement';
import PromotionsManagement from '../Admin/PromotionsManagement';
import PaymentMethodsManagement from '../Admin/PaymentMethodsManagement';
import PaymentOptionsManagement from '../Admin/PaymentOptionsManagement';
import PaymentTransactionsManagement from '../Admin/PaymentTransactionsManagement';
import EMIPaymentsManagement from '../Admin/EMIPaymentsManagement';
import SplitPaymentsManagement from '../Admin/SplitPaymentsManagement';
import OrderNotificationPopup from '../Admin/OrderNotificationPopup';
import OrdersManagement from '../Admin/OrdersManagement';
import BlogPageManagement from '../Admin/BlogPageManagement';
import ContactPageManagement from '../Admin/ContactPageManagement';
import QuotePageManagement from '../Admin/QuotePageManagement';
import NotFoundPageManagement from '../Admin/NotFoundPageManagement';
import ContactFormOptionsManagement from '../Admin/ContactFormOptionsManagement';
import ContactFormFieldsManagement from '../Admin/ContactFormFieldsManagement';
import MaintenancePageManagement from '../Admin/MaintenancePageManagement';
import QuoteStepsManagement from '../Admin/QuoteStepsManagement';
import QuoteContentManagement from '../Admin/QuoteContentManagement';
import PDFTemplateManagement from '../Admin/PDFTemplateManagement';
import PagesManagement from '../Admin/PagesManagement';
import ComingSoonPageManagement from '../Admin/ComingSoonPageManagement';
import AppointmentPageManagement from '../Admin/AppointmentPageManagement';
import AppointmentsManagement from '../Admin/AppointmentsManagement';
import QuotesManagement from '../Admin/QuotesManagement';
import NewsletterManagement from '../Admin/NewsletterManagement';
import FooterManagement from '../Admin/FooterManagement';
import LoadingScreenManagement from '../Admin/LoadingScreenManagement';
import ThemeManagement from '../Admin/ThemeManagement';
import WebPushNotificationManagement from '../Admin/WebPushNotificationManagement';
import SecurityManagement from '../Admin/SecurityManagement';
import SpecialDaysManagement from '../Admin/SpecialDaysManagement';
import CouponsManagement from '../Admin/CouponsManagement';
import CouponAnalytics from '../Admin/CouponAnalytics';
import DualSmtpManagement from '../Admin/DualSmtpManagement';
import WhatsAppManagement from '../Admin/WhatsAppManagement';
import WhatsAppChat from '../Admin/WhatsAppChat';
import WhatsAppAnalytics from '../Admin/WhatsAppAnalytics';
import WhatsAppBroadcast from '../Admin/WhatsAppBroadcast';
import WhatsAppAutoReply from '../Admin/WhatsAppAutoReply';
import WhatsAppEcommerce from '../Admin/WhatsAppEcommerce';
import WhatsAppMarketing from '../Admin/WhatsAppMarketing';
import WhatsAppAdvancedFeatures from '../Admin/WhatsAppAdvancedFeatures';
import WhatsAppContacts from '../Admin/WhatsAppContacts';
import WhatsAppAiAssistants from '../Admin/WhatsAppAiAssistants';
import WhatsAppTemplates from '../Admin/WhatsAppTemplates';
import FloatingChatManagement from '../Admin/FloatingChatManagement';
import S3Settings from '../Admin/S3Settings';
import FileManagement from '../Admin/FileManagement';
import QuotationsManagement from '../Admin/QuotationsManagement';
import InvoicesManagement from '../Admin/InvoicesManagement';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { settings: siteSettings, getLogoUrl, loading: settingsLoading } = useSiteSettings();
  const { formatPrice } = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [darkMode, setDarkMode] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [orderNotification, setOrderNotification] = useState(null);
  const [lastProcessedOrderId, setLastProcessedOrderId] = useState(null);

  // Fetch admin dashboard data
  const { data: dashboardData, loading: dashboardLoading, refetch: refetchDashboard } = useApiCall(
    adminAPI.getDashboard,
    [],
    true
  );

  const { data: analytics, loading: analyticsLoading } = useApiCall(
    () => adminAPI.getAnalytics({ period: 30 }),
    [],
    true
  );

  // Order notification system
  const { isConnected, connectionStatus, lastMessage } = useOrderNotifications();
  const { notify } = useNotificationHook();

  // Fetch real notifications
  const { data: notificationsData, loading: notificationsLoading, error: notificationsError, refetch: refetchNotifications } = useApiCall(
    () => adminAPI.getNotifications({ limit: 10 }),
    [],
    true
  );

  // Debug the actual response structure
  useEffect(() => {
    console.log('🔍 Raw notifications data:', notificationsData);
    console.log('🔍 Notifications data structure:', {
      notifications: notificationsData?.notifications,
      unread: notificationsData?.unread,
      total: notificationsData?.total,
      pagination: notificationsData?.pagination
    });
  }, [notificationsData]);

  // The useApiCall hook returns the data directly, not wrapped in response.data
  const notifications = notificationsData?.notifications || [];
  const unreadCount = notificationsData?.unread || 0;

  // Debug notifications
  useEffect(() => {
    console.log('🔍 Notifications debug:', {
      data: notificationsData,
      loading: notificationsLoading,
      error: notificationsError,
      notifications: notifications,
      unreadCount: unreadCount
    });
  }, [notificationsData, notificationsLoading, notificationsError, notifications, unreadCount]);

  // Handle dark mode toggle
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  };

  // Handle incoming order notifications
  useEffect(() => {
    console.log('🔍 Checking lastMessage:', lastMessage);
    if (lastMessage && lastMessage.type === 'new_order') {
      console.log('📦 New order notification received:', lastMessage.order);
      
      // Prevent duplicate notifications for the same order
      if (lastProcessedOrderId === lastMessage.order.id) {
        console.log('🔄 Duplicate order notification ignored:', lastMessage.order.order_number);
        return;
      }
      
      // Update last processed order ID
      setLastProcessedOrderId(lastMessage.order.id);
      
      // Show only popup notification (removed duplicate toast notification)
      setOrderNotification(lastMessage.order);
      
      // Optional: Show a subtle toast notification without actions to avoid duplication
      // Uncomment the following lines if you want both notifications
      /*
      notify.success(
        `New order received: ${lastMessage.order.order_number}`,
        'New Order',
        {
          duration: 3000,
          actions: [
            {
              label: 'View Order',
              type: 'primary',
              onClick: () => {
                setOrderNotification(lastMessage.order);
              }
            }
          ]
        }
      );
      */
    }
  }, [lastMessage, notify, lastProcessedOrderId]);



  // Handle notification click
  const handleNotificationClick = async (notification) => {
    console.log('🔔 Notification clicked:', notification);
    
    try {
      // Mark notification as read
      await adminAPI.markNotificationAsRead(notification.id);
      console.log('✅ Notification marked as read');
      
      // Refresh notifications to update the count
      refetchNotifications();
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
    }
    
    // Close notifications dropdown
    setShowNotifications(false);
    
    // Navigate based on notification type
    switch (notification.type) {
      case 'user_registration':
        navigate('/admin/users');
        break;
      case 'contact_inquiry':
        navigate('/admin/contacts');
        break;
      case 'quote_request':
        navigate('/admin/quotes');
        break;
      case 'new_order':
        navigate('/admin/orders');
        break;
      case 'appointment_request':
        navigate('/admin/appointments');
        break;
      default:
        console.log('Unknown notification type:', notification.type);
    }
  };

  // Handle view all notifications
  const handleViewAllNotifications = async () => {
    console.log('🔔 View all notifications clicked');
    
    try {
      // Mark all notifications as read
      await adminAPI.markAllNotificationsAsRead();
      console.log('✅ All notifications marked as read');
      
      // Refresh notifications to update the count
      refetchNotifications();
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
    }
    
    setShowNotifications(false);
    // Navigate to a dedicated notifications page or show a modal
    // For now, we'll just show an alert
    alert('View All Notifications - This feature will be implemented soon!');
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Initialize dark mode from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowNotifications(false);
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { data: systemHealth, loading: healthLoading } = useApiCall(
    adminAPI.getHealth,
    [],
    true
  );

  useEffect(() => {
    const path = location.pathname.split('/admin/')[1] || 'overview';
    setActiveSection(path);
  }, [location]);

  const formatCurrency = (amount) => {
    return formatPrice(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'published':
      case 'active':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'in_progress':
      case 'processing':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20';
      case 'pending':
      case 'draft':
        return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'cancelled':
      case 'failed':
      case 'inactive':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  // Handle section collapse/expand
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Reorganized sidebar items with logical groups
  const sidebarGroups = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      items: [
        { id: 'overview', label: 'Overview', icon: BarChart3, path: '/admin' },
        { id: 'analytics', label: 'Analytics', icon: TrendingUp, path: '/admin/analytics' },
      ]
    },
    {
      id: 'content-management',
      label: 'Content Management',
      icon: FileText,
      items: [
        { id: 'blog', label: 'Blog', icon: FileText, path: '/admin/blog' },
        { id: 'portfolio', label: 'Portfolio', icon: Eye, path: '/admin/portfolio' },
        { id: 'testimonials', label: 'Testimonials', icon: Star, path: '/admin/testimonials' },
        { id: 'services', label: 'Services', icon: Globe, path: '/admin/services' },
      ]
    },
    {
      id: 'ecommerce',
      label: 'E-commerce',
      icon: ShoppingBag,
      items: [
        { id: 'products', label: 'Products', icon: ShoppingBag, path: '/admin/products' },
        { id: 'product-categories', label: 'Product Categories', icon: Tag, path: '/admin/products/categories' },
        { id: 'orders', label: 'Orders', icon: Package, path: '/admin/orders' },
        { id: 'promotions', label: 'Promotions', icon: Tag, path: '/admin/promotions' },
        { id: 'coupons', label: 'Coupon Management', icon: Tag, path: '/admin/coupons' },
        { id: 'coupon-analytics', label: 'Coupon Analytics', icon: BarChart, path: '/admin/coupon-analytics' },
        { id: 'quotations', label: 'Quotations', icon: FileText, path: '/admin/quotations' },
        { id: 'invoices', label: 'Invoices', icon: FileText, path: '/admin/invoices' },
      ]
    },
    {
      id: 'payments',
      label: 'Payments',
      icon: DollarSign,
      items: [
        { id: 'payment-transactions', label: 'Payment Transactions', icon: DollarSign, path: '/admin/payment-transactions' },
        { id: 'emi-payments', label: 'EMI Payments', icon: CreditCard, path: '/admin/emi-payments' },
        { id: 'split-payments', label: 'Split Payments', icon: CreditCard, path: '/admin/split-payments' },
      ]
    },
    {
      id: 'user-management',
      label: 'User Management',
      icon: Users,
      items: [
        { id: 'users', label: 'Users', icon: Users, path: '/admin/users' },
        { id: 'projects', label: 'Projects', icon: FileText, path: '/admin/projects' },
        { id: 'project-types', label: 'Project Types', icon: Settings, path: '/admin/project-types' },
      ]
    },
    {
      id: 'communication',
      label: 'Communication',
      icon: MessageSquare,
      items: [
        { id: 'messages', label: 'Messages', icon: MessageSquare, path: '/admin/contacts' },
        { id: 'quotes', label: 'Quotes', icon: FileText, path: '/admin/quotes' },
        { id: 'appointments', label: 'Appointments', icon: Calendar, path: '/admin/appointments' },
        { id: 'newsletter', label: 'Newsletter', icon: Mail, path: '/admin/newsletter' },
        { id: 'web-push-notifications', label: 'Web Push Notifications', icon: Bell, path: '/admin/web-push-notifications' },
        { id: 'whatsapp', label: 'WhatsApp Management', icon: MessageSquare, path: '/admin/whatsapp' },
                                { id: 'whatsapp-chat', label: 'WhatsApp Chat', icon: MessageSquare, path: '/admin/whatsapp-chat' },
                        { id: 'whatsapp-analytics', label: 'WhatsApp Analytics', icon: BarChart3, path: '/admin/whatsapp-analytics' },
                                { id: 'whatsapp-broadcast', label: 'WhatsApp Broadcast', icon: Send, path: '/admin/whatsapp-broadcast' },
        { id: 'whatsapp-auto-reply', label: 'WhatsApp Auto-Reply', icon: Bot, path: '/admin/whatsapp-auto-reply' },
        { id: 'whatsapp-ecommerce', label: 'WhatsApp E-Commerce', icon: ShoppingCart, path: '/admin/whatsapp-ecommerce' },
        { id: 'whatsapp-marketing', label: 'WhatsApp Marketing', icon: TrendingUp, path: '/admin/whatsapp-marketing' },
        { id: 'whatsapp-advanced-features', label: 'WhatsApp Advanced Features', icon: Zap, path: '/admin/whatsapp-advanced-features' },
        { id: 'whatsapp-contacts', label: 'WhatsApp Contacts', icon: Users, path: '/admin/whatsapp-contacts' },
        { id: 'whatsapp-ai-assistants', label: 'AI Assistants', icon: Bot, path: '/admin/whatsapp-ai-assistants' },
        { id: 'whatsapp-templates', label: 'WhatsApp Templates', icon: FileText, path: '/admin/whatsapp-templates' },
        { id: 'floating-chat', label: 'Floating Chat Settings', icon: MessageSquare, path: '/admin/floating-chat' },
      ]
    },
    {
      id: 'website-pages',
      label: 'Website Pages',
      icon: Layout,
      items: [
        { id: 'homepage', label: 'Home Page', icon: Home, path: '/admin/homepage' },
        { id: 'about-us', label: 'About Us Page', icon: Users, path: '/admin/about-us' },
        { id: 'services-page', label: 'Services Page', icon: Settings, path: '/admin/services-page' },
        { id: 'products-page', label: 'Products Page', icon: ShoppingBag, path: '/admin/products-page' },
        { id: 'portfolio-page', label: 'Portfolio Page', icon: Eye, path: '/admin/portfolio-page' },
        { id: 'blog-page', label: 'Blog Page', icon: FileText, path: '/admin/blog-page' },
        { id: 'contact-page', label: 'Contact Page', icon: MessageSquare, path: '/admin/contact-page' },
        { id: 'appointment-page', label: 'Appointment Page', icon: Calendar, path: '/admin/appointment-page' },
        { id: 'quote-page', label: 'Quote Page', icon: Calculator, path: '/admin/quote-page' },
        { id: 'coming-soon-page', label: 'Coming Soon Page', icon: Clock, path: '/admin/coming-soon-page' },
        { id: 'not-found-page', label: 'Not Found Page', icon: AlertTriangle, path: '/admin/not-found-page' },
        { id: 'maintenance-page', label: 'Maintenance Mode Page', icon: Settings, path: '/admin/maintenance-page' },
      ]
    },
    {
      id: 'forms-configuration',
      label: 'Forms & Configuration',
      icon: FormInput,
      items: [
        { id: 'contact-form-options', label: 'Contact Form Options', icon: Settings, path: '/admin/contact-form-options' },
        { id: 'contact-form-fields', label: 'Contact Form Fields', icon: FormInput, path: '/admin/contact-form-fields' },
        { id: 'quote-steps', label: 'Quote Steps', icon: Settings, path: '/admin/quote-steps' },
        { id: 'quote-content', label: 'Quote Content', icon: Package, path: '/admin/quote-content' },
        { id: 'pdf-templates', label: 'PDF Templates', icon: FileText, path: '/admin/pdf-templates' },
      ]
    },
    {
      id: 'system',
      label: 'System',
      icon: Settings,
      items: [
        { id: 'theme-manager', label: 'Theme Manager', icon: Palette, path: '/admin/theme-manager' },
        { id: 'pages', label: 'Pages Management', icon: FileText, path: '/admin/pages' },
        { id: 'footer', label: 'Footer Management', icon: Settings, path: '/admin/footer' },
        { id: 'loading-screen', label: 'Loading Screen', icon: Settings, path: '/admin/loading-screen' },
        { id: 'special-days', label: 'Special Days', icon: Calendar, path: '/admin/special-days' },
        { id: 'payment-methods', label: 'Payment Methods', icon: CreditCard, path: '/admin/payment-methods' },
        { id: 'payment-options', label: 'Payment Options', icon: CreditCard, path: '/admin/payment-options' },
        { id: 'settings', label: 'Settings', icon: Settings, path: '/admin/settings' },
        { id: 'ai-management', label: 'AI Management', icon: Bot, path: '/admin/ai-management' },
        { id: 's3-settings', label: 'S3 Storage Settings', icon: Database, path: '/admin/s3-settings' },
        { id: 'file-management', label: 'File Management', icon: FileText, path: '/admin/file-management' },
        { id: 'dual-smtp', label: 'Dual SMTP Management', icon: Mail, path: '/admin/dual-smtp' },
        { id: 'security', label: 'Security Management', icon: Shield, path: '/admin/security' },
      ]
    },
  ];

  // Filter sidebar groups based on search query
  const filteredSidebarGroups = sidebarGroups.map(group => {
    const filteredItems = group.items.filter(item => {
      const itemMatches = item.label.toLowerCase().includes(searchQuery.toLowerCase());
      return itemMatches;
    });

    return {
      ...group,
      items: filteredItems
    };
  }).filter(group => group.items.length > 0);

  // Check if any group has active items for highlighting
  const hasActiveItemInGroup = (group) => {
    return group.items.some(item => activeSection === item.id);
  };

  // Get the appropriate favicon and title based on current settings
  const currentFavicon = !settingsLoading && siteSettings?.favicon ? siteSettings.favicon : null;
  const currentSiteTitle = !settingsLoading && siteSettings?.siteTitle ? siteSettings.siteTitle : 'Niswa Admin';

  // Show loading state while settings are being fetched
  if (settingsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="h-5 w-5 text-white" />
          </div>
          <p className="text-gray-600 dark:text-gray-400">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Menu Bar */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
          {/* Left Side - Logo and Menu Toggle */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 lg:hidden touch-manipulation"
            >
              {sidebarOpen ? <X className="h-4 w-4 sm:h-5 sm:w-5" /> : <Menu className="h-4 w-4 sm:h-5 sm:w-5" />}
            </button>
            <div className="flex items-center space-x-2 sm:space-x-3">
              {currentFavicon ? (
                <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center">
                  <img 
                    src={currentFavicon} 
                    alt={currentSiteTitle} 
                    className="h-6 w-auto max-w-6 sm:h-8 sm:max-w-8 object-contain"
                    onError={(e) => {
                      // Fallback to default icon if image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center hidden">
                    <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                </div>
              ) : (
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {currentSiteTitle}
                </h1>
              </div>
            </div>
          </div>

          {/* Right Side - Actions */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* WebSocket Connection Status */}
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-500' : connectionStatus === 'connecting' ? 'bg-yellow-500' : 'bg-red-500'
              }`}></div>
              <span className="text-xs text-gray-600 dark:text-gray-400 hidden sm:block">
                {isConnected ? 'Connected' : connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
              </span>

            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors touch-manipulation"
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? (
                <Sun className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Moon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            {/* Notifications */}
            <div className="relative dropdown-container">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative touch-manipulation"
                title="Notifications"
              >
                <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-3 w-3 sm:h-4 sm:w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">Notifications</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notificationsLoading ? (
                      <div className="p-3 sm:p-4">
                        <div className="animate-pulse space-y-2 sm:space-y-3">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-start space-x-2 sm:space-x-3">
                              <div className="h-4 w-4 sm:h-5 sm:w-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                              <div className="flex-1 space-y-1 sm:space-y-2">
                                <div className="h-3 sm:h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                                <div className="h-2 sm:h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : notificationsError ? (
                      <div className="p-3 sm:p-4 text-center text-red-500 dark:text-red-400">
                        <p className="text-xs sm:text-sm">Error loading notifications</p>
                        <p className="text-xs mt-1">{notificationsError}</p>
                        <button 
                          onClick={() => refetchNotifications()}
                          className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline touch-manipulation"
                        >
                          Try again
                        </button>
                      </div>
                    ) : notifications.length > 0 ? (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`p-3 sm:p-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors touch-manipulation ${
                            !notification.read ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                          }`}
                        >
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              {notification.type === 'new_order' && (
                                <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
                              )}
                              {notification.type === 'user_registration' && (
                                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                              )}
                              {notification.type === 'contact_inquiry' && (
                                <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
                              )}
                              {notification.type === 'appointment_request' && (
                                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500" />
                              )}
                              {notification.type === 'quote_request' && (
                                <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-500" />
                              )}
                            </div>
                            <div className="ml-2 sm:ml-3 flex-1">
                              <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                                {notification.title}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 sm:mt-2">
                                {notification.time}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-3 sm:p-4 text-center text-gray-500 dark:text-gray-400">
                        <Bell className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-xs sm:text-sm">No notifications</p>
                      </div>
                    )}
                  </div>
                  <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <button 
                      onClick={() => refetchNotifications()}
                      disabled={notificationsLoading}
                      className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center touch-manipulation"
                    >
                      {notificationsLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                          <span className="hidden sm:inline">Refreshing...</span>
                          <span className="sm:hidden">...</span>
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-3 w-3 mr-1" />
                          <span className="hidden sm:inline">Refresh</span>
                        </>
                      )}
                    </button>
                    <button 
                      onClick={handleViewAllNotifications}
                      className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 touch-manipulation"
                    >
                      <span className="hidden sm:inline">View all notifications</span>
                      <span className="sm:hidden">View all</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative dropdown-container">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-1 sm:space-x-2 p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors touch-manipulation"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <User className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                </div>
                <span className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-20">
                  {user?.name}
                </span>
                <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-40 sm:w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  <div className="py-2">
                    <div className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      Signed in as <span className="font-medium truncate block">{user?.email}</span>
                    </div>
                    <hr className="my-2 border-gray-200 dark:border-gray-700" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-3 sm:px-4 py-2 text-xs sm:text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 touch-manipulation"
                    >
                      <LogOut className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex flex-1">
        {/* Modern Sidebar */}
        <div className={`${sidebarOpen ? 'w-64 sm:w-72' : 'w-0'} lg:w-72 bg-white dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-hidden fixed lg:relative z-50 lg:z-auto h-full lg:h-auto`}>
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                  Admin Panel
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Welcome back, <span className="truncate block">{user?.name}</span>
                </p>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                <Search className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search menus..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-8 sm:pl-10 pr-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs sm:text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-2 sm:pr-3 flex items-center touch-manipulation"
                >
                  <X className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                </button>
              )}
            </div>
          </div>
        
          <nav className="p-3 sm:p-4 space-y-1 sm:space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
            {filteredSidebarGroups.length > 0 ? (
              filteredSidebarGroups.map((group) => {
                const GroupIcon = group.icon;
                const isGroupCollapsed = collapsedSections[group.id];
                const hasActiveItem = hasActiveItemInGroup(group);

                return (
                  <div key={group.id} className="space-y-1">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleSection(group.id)}
                      className={`w-full flex items-center justify-between px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 touch-manipulation ${
                        hasActiveItem
                          ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <GroupIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="truncate">{group.label}</span>
                        {searchQuery && (
                          <span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">
                            {group.items.length}
                          </span>
                        )}
                      </div>
                      {isGroupCollapsed ? (
                        <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200" />
                      ) : (
                        <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200" />
                      )}
                    </button>

                    {/* Group Items */}
                    {!isGroupCollapsed && (
                      <div className="ml-4 sm:ml-6 space-y-1">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = activeSection === item.id;
                          
                          return (
                            <div key={item.id}>
                              <Link
                                to={item.path}
                                className={`flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm transition-all duration-200 touch-manipulation ${
                                  isActive
                                    ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20 font-medium'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                <Icon className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3" />
                                <span className="truncate">{item.label}</span>
                              </Link>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 sm:py-8">
                <Search className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">
                  No menus found for "{searchQuery}"
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-2 text-blue-600 dark:text-blue-400 text-xs sm:text-sm hover:underline touch-manipulation"
                >
                  Clear search
                </button>
              </div>
            )}
          </nav>
        </div>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <Routes>
            <Route path="/" element={<OverviewSection dashboardData={dashboardData} loading={dashboardLoading} />} />
            <Route path="/users" element={<UsersManagement />} />
            <Route path="/projects" element={<ProjectsManagement />} />
            <Route path="/project-types" element={<ProjectTypesManagement />} />
            <Route path="/contacts" element={<ContactsManagement />} />
            <Route path="/quotes" element={<QuotesManagement />} />
            <Route path="/appointments" element={<AppointmentsManagement />} />
                    <Route path="/newsletter" element={<NewsletterManagement />} />
        <Route path="/web-push-notifications" element={<WebPushNotificationManagement />} />
        <Route path="/whatsapp" element={<WhatsAppManagement />} />
                                      <Route path="/whatsapp-chat" element={<WhatsAppChat />} />
                          <Route path="/whatsapp-analytics" element={<WhatsAppAnalytics />} />
                                  <Route path="/whatsapp-broadcast" element={<WhatsAppBroadcast />} />
        <Route path="/whatsapp-auto-reply" element={<WhatsAppAutoReply />} />
        <Route path="/whatsapp-ecommerce" element={<WhatsAppEcommerce />} />
        <Route path="/whatsapp-marketing" element={<WhatsAppMarketing />} />
        <Route path="/whatsapp-advanced-features" element={<WhatsAppAdvancedFeatures />} />
        <Route path="/whatsapp-contacts" element={<WhatsAppContacts />} />
                <Route path="/whatsapp-ai-assistants" element={<WhatsAppAiAssistants />} />
        <Route path="/whatsapp-templates" element={<WhatsAppTemplates />} />
        <Route path="/floating-chat" element={<FloatingChatManagement />} />
              <Route path="/blog" element={<BlogManagement />} />
            <Route path="/products" element={<ProductsManagement />} />
            <Route path="/products/categories" element={<ProductCategoriesManagement />} />
            <Route path="/quotations" element={<QuotationsManagement />} />
            <Route path="/invoices" element={<InvoicesManagement />} />
            <Route path="/services" element={<ServicesManagement />} />
            <Route path="/portfolio" element={<PortfolioManagement />} />
            <Route path="/testimonials" element={<TestimonialsManagement />} />
            <Route path="/homepage" element={<HomePageManagement />} />
            <Route path="/about-us" element={<AboutUsManagement />} />
            <Route path="/services-page" element={<ServicesPageManagement />} />
            <Route path="/products-page" element={<ProductsPageManagement />} />
            <Route path="/portfolio-page" element={<PortfolioPageManagement />} />
            <Route path="/blog-page" element={<BlogPageManagement />} />
            <Route path="/contact-page" element={<ContactPageManagement />} />
            <Route path="/appointment-page" element={<AppointmentPageManagement />} />
            <Route path="/quote-page" element={<QuotePageManagement />} />
            <Route path="/not-found-page" element={<NotFoundPageManagement />} />
            <Route path="/maintenance-page" element={<MaintenancePageManagement />} />
            <Route path="/contact-form-options" element={<ContactFormOptionsManagement />} />
            <Route path="/contact-form-fields" element={<ContactFormFieldsManagement />} />
            <Route path="/theme-manager" element={<ThemeManagement />} />
            <Route path="/footer" element={<FooterManagement />} />
            <Route path="/loading-screen" element={<LoadingScreenManagement />} />
            <Route path="/special-days" element={<SpecialDaysManagement />} />
            <Route path="/quote-steps" element={<QuoteStepsManagement />} />
            <Route path="/quote-content" element={<QuoteContentManagement />} />
            <Route path="/pdf-templates" element={<PDFTemplateManagement />} />
            <Route path="/pages" element={<PagesManagement />} />
            <Route path="/coming-soon-page" element={<ComingSoonPageManagement />} />
            <Route path="/promotions" element={<PromotionsManagement />} />
        <Route path="/coupons" element={<CouponsManagement />} />
        <Route path="/coupon-analytics" element={<CouponAnalytics />} />
            <Route path="/payment-methods" element={<PaymentMethodsManagement />} />
            <Route path="/payment-options" element={<PaymentOptionsManagement />} />
            <Route path="/payment-transactions" element={<PaymentTransactionsManagement />} />
            <Route path="/emi-payments" element={<EMIPaymentsManagement />} />
            <Route path="/split-payments" element={<SplitPaymentsManagement />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />
            <Route path="/settings" element={<SettingsManagement />} />
            <Route path="/s3-settings" element={<S3Settings />} />
            <Route path="/file-management" element={<FileManagement />} />
            <Route path="/orders" element={<OrdersManagement />} />
        <Route path="/dual-smtp" element={<DualSmtpManagement />} />
        <Route path="/security" element={<SecurityManagement />} />
        <Route path="/ai-management" element={<AIManagement />} />
          </Routes>
        </div>
      </div>

      {/* Order Notification Popup */}
      {orderNotification && (
        <OrderNotificationPopup
          order={orderNotification}
          onClose={() => setOrderNotification(null)}
          onViewOrder={(order) => {
            setOrderNotification(null);
            navigate('/admin/orders');
          }}
        />
      )}
    </div>
  );
};

// Overview Section Component
const OverviewSection = ({ dashboardData, loading }) => {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div className="p-4 sm:p-6 md:p-8">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <div className="h-3 sm:h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-6 sm:h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const overview = dashboardData?.overview || {};

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
      {/* Enhanced Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Dashboard Overview
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2 text-base sm:text-lg">
              Monitor your business metrics and performance in real-time
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-green-600 dark:text-green-400 font-medium">Live</span>
          </div>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="group relative bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-200 dark:border-blue-700 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="text-right">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">
                Total Users
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {overview.users || 0}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                +12% from last month
              </p>
            </div>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-green-200 dark:border-green-700 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg">
                <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="text-right">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 mb-1">
                Active Projects
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {overview.projects || 0}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                +8% from last month
              </p>
            </div>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-yellow-200 dark:border-yellow-700 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg">
                <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="text-right">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
              </div>
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-yellow-600 dark:text-yellow-400 mb-1">
                New Inquiries
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {overview.contacts || 0}
              </p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                +15% from last month
              </p>
            </div>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-200 dark:border-purple-700 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
                <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="text-right">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400 mb-1">
                Total Revenue
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                ₹{(overview.revenue || 0).toLocaleString()}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                +23% from last month
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <Zap className="h-5 w-5 mr-2 text-green-600 dark:text-green-400" />
            Quick Actions
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button 
              onClick={() => navigate('/admin/projects')}
              className="flex items-center justify-center p-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <Plus className="h-5 w-5 mr-2" />
              <span className="font-medium">Add Project</span>
            </button>
            <button 
              onClick={() => navigate('/admin/contacts')}
              className="flex items-center justify-center p-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <MessageSquare className="h-5 w-5 mr-2" />
              <span className="font-medium">View Messages</span>
            </button>
            <button 
              onClick={() => navigate('/admin/analytics')}
              className="flex items-center justify-center p-4 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <BarChart3 className="h-5 w-5 mr-2" />
              <span className="font-medium">Analytics</span>
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Content Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <Activity className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600 dark:text-blue-400" />
                Recent Activity
              </h3>
              <span className="px-2 sm:px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full">
                Live
              </span>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            {dashboardData?.recentActivity?.length > 0 ? (
              <div className="space-y-3 sm:space-y-4">
                {dashboardData.recentActivity.slice(0, 5).map((activity, index) => (
                  <div key={activity.id} className="flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200">
                    <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg">
                      <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                        {activity.user_name || 'System'} {activity.action.replace('_', ' ')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 sm:py-8">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                </div>
                <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">No recent activity</p>
              </div>
            )}
          </div>
        </div>

        {/* Pending Items */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-orange-600 dark:text-orange-400" />
                Pending Items
              </h3>
              <span className="px-2 sm:px-3 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400 rounded-full">
                {dashboardData?.pendingItems?.total || 0} pending
              </span>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/10 dark:to-yellow-800/10 rounded-xl border border-yellow-200 dark:border-yellow-700">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 sm:p-2 bg-yellow-500 rounded-lg">
                    <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                    New Quotes
                  </span>
                </div>
                <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold bg-yellow-500 text-white rounded-full">
                  {dashboardData?.pendingItems?.quotes || 0}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/10 dark:to-blue-800/10 rounded-xl border border-blue-200 dark:border-blue-700">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 sm:p-2 bg-blue-500 rounded-lg">
                    <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                    New Contacts
                  </span>
                </div>
                <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold bg-blue-500 text-white rounded-full">
                  {dashboardData?.pendingItems?.contacts || 0}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/10 dark:to-purple-800/10 rounded-xl border border-purple-200 dark:border-purple-700">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 sm:p-2 bg-purple-500 rounded-lg">
                    <Star className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                    Pending Reviews
                  </span>
                </div>
                <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold bg-purple-500 text-white rounded-full">
                  {dashboardData?.pendingItems?.reviews || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};



export default AdminDashboard;
